import { useMemo, useState } from "react";
import { toast } from "sonner";
import { MENU } from "@/data/content";
import { joinHouseAlerts } from "@/lib/api";

/* ------------------------- assets ------------------------- */
const VAULT_DOORS =
  "https://customer-assets.emergentagent.com/job_the-house-list/artifacts/prfkl11a_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2004_06_17%20PM.png";
const VAULT_INTERIOR =
  "https://customer-assets.emergentagent.com/job_the-house-list/artifacts/yd0c6qz4_image.png";

const PRODUCT_DRINK = {
  name: "Lavender Honey Matcha — N° 03",
  caption: "Ceremonial matcha, dusty lavender, raw honey.",
  src: "/vault/drink.png",
};
const PRODUCT_DESSERT = {
  name: "Banana Pudding — Dessert Cake",
  caption: "Vanilla pudding, ripe banana, vanilla wafers, whipped cream. 16 oz.",
  src: "/vault/dessert.png",
};

const COLD_BREWS = MENU.find((m) => m.key === "cold-brew")?.items || [];

/* =========================================================
   THE VAULT — one page, three sections
   1) The Vault Showcase (with door animation)
   2) The Standing Menu (cold brews)
   3) The Dispatch (SMS subscription)
   ========================================================= */
export default function Vault() {
  return (
    <div data-testid="vault-page" className="pt-28 md:pt-36">
      <VaultShowcase />
      <StandingMenu />
      <Dispatch />
    </div>
  );
}

/* ----------------------- 1) Showcase ---------------------- */
function VaultShowcase() {
  // states: idle -> spinning -> opening -> open -> closing -> idle
  const [state, setState] = useState("idle");

  const open = () => {
    if (state !== "idle") return;
    setState("spinning");
    window.setTimeout(() => setState("opening"), 2400);   // after wheel spin
    window.setTimeout(() => setState("open"), 2400 + 1600); // after doors swing
  };
  const close = () => {
    if (state !== "open") return;
    setState("closing");
    window.setTimeout(() => setState("idle"), 1600);
  };

  const doorsSwung = state === "opening" || state === "open";
  const wheelVisible = state === "idle" || state === "spinning";
  const wheelSpin = state === "spinning";
  const ctaBusy = state === "spinning" || state === "opening" || state === "closing";
  const isOpen = state === "open";

  return (
    <section data-testid="vault-showcase" className="px-6 md:px-10 pb-24 md:pb-32">
      <div className="mx-auto max-w-[1400px]">
        <p className="label-eyebrow">§ I · The Vault</p>
        <h1 className="font-display text-bone text-5xl md:text-7xl lg:text-[88px] mt-5 leading-[0.95]">
          What lies behind the <em className="not-italic text-brass">brass door.</em>
        </h1>
        <p className="mt-6 max-w-2xl text-bone/65 text-base md:text-lg leading-relaxed">
          Drops are released without warning. Quantities are deliberately insufficient. Turn the dial.
          See what the House offers tonight.
        </p>

        {/* the door stage */}
        <div className="mt-14 relative" data-testid="vault-stage">
          <div
            className="relative mx-auto w-full max-w-[820px] aspect-square overflow-hidden bg-obsidian"
            style={{ perspective: "2200px", transformStyle: "preserve-3d" }}
          >
            {/* INTERIOR — the lavender chamber + cans on the table */}
            <VaultInterior shown={doorsSwung} fullyOpen={isOpen} />

            {/* DOORS — two halves of the same image that swing apart */}
            <DoorHalf side="left" swung={doorsSwung} />
            <DoorHalf side="right" swung={doorsSwung} />

            {/* BRASS WHEEL OVERLAY — spins on idle->spinning, fades when doors open */}
            <WheelOverlay visible={wheelVisible} spinning={wheelSpin} />

            {/* fanfare sparkles while opening + open */}
            <Sparkles active={doorsSwung} />
          </div>

          {/* CTA */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              type="button"
              data-testid="vault-cta"
              onClick={isOpen ? close : open}
              disabled={ctaBusy}
              className="inline-flex items-center gap-3 px-8 py-4 bg-brass text-obsidian text-[12px] uppercase tracking-[0.3em] hover:bg-champagne transition-colors disabled:opacity-60"
            >
              {state === "idle" && "Turn Vault Wheel"}
              {state === "spinning" && "Tumblers Falling…"}
              {state === "opening" && "Doors Opening…"}
              {state === "open" && "Lock the Vault"}
              {state === "closing" && "Doors Closing…"}
            </button>
          </div>
        </div>
      </div>

      <VaultStyles />
    </section>
  );
}

/* One half of the doors image — swings outward on its outer hinge */
function DoorHalf({ side, swung }) {
  const isLeft = side === "left";
  return (
    <div
      aria-hidden="true"
      data-testid={`vault-door-${side}`}
      className="vault-door-half absolute inset-0"
      style={{
        backgroundImage: `url("${VAULT_DOORS}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        clipPath: isLeft ? "inset(0 50% 0 0)" : "inset(0 0 0 50%)",
        transformOrigin: isLeft ? "left center" : "right center",
        transform: swung
          ? `perspective(2200px) rotateY(${isLeft ? -115 : 115}deg)`
          : "perspective(2200px) rotateY(0deg)",
        transition: "transform 1.6s cubic-bezier(0.65, 0, 0.25, 1)",
        backfaceVisibility: "hidden",
        zIndex: 20,
        willChange: "transform",
        boxShadow: swung ? "none" : "inset 0 0 80px rgba(0,0,0,0.45)",
      }}
    />
  );
}

/* A brass wheel overlay positioned exactly over the doors' wheel.
   Spins during the spinning state, fades when the doors swing open. */
function WheelOverlay({ visible, spinning }) {
  return (
    <div
      aria-hidden="true"
      data-testid="vault-wheel-overlay"
      className="vault-wheel-overlay absolute"
      style={{
        left: "50%",
        top: "58%",
        transform: "translate(-50%, -50%)",
        width: "min(72%, 580px)",
        aspectRatio: "1 / 1",
        zIndex: 25,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.5s ease",
        pointerEvents: "none",
      }}
    >
      <img
        src="https://customer-assets.emergentagent.com/job_the-house-list/artifacts/m1uvou0l_Holiday%20Signatures%20%285%29.png"
        alt=""
        draggable={false}
        className="vault-wheel-img select-none"
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "contain",
          mixBlendMode: "screen",
          animation: spinning
            ? "vault-wheel-spin 2.4s cubic-bezier(0.16, 1, 0.3, 1) forwards"
            : "none",
          filter: "drop-shadow(0 0 24px rgba(176,141,70,0.35))",
          transformOrigin: "center center",
          willChange: "transform",
        }}
      />
    </div>
  );
}

/* The interior — lavender chamber + two cans on the wooden table */
function VaultInterior({ shown, fullyOpen }) {
  return (
    <div className="absolute inset-0 vault-interior" data-testid="vault-interior">
      <img
        src={VAULT_INTERIOR}
        alt=""
        draggable={false}
        className="block w-full h-full object-cover select-none"
      />

      {/* warm spotlight wash to lift the products off the velvet */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 transition-opacity duration-1000 ${shown ? "opacity-100" : "opacity-0"}`}
        style={{
          background:
            "radial-gradient(35% 22% at 50% 58%, rgba(255,231,180,0.32), transparent 70%)",
          transitionDelay: shown ? "0.4s" : "0s",
        }}
      />

      {/* cans on the wooden table */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${fullyOpen ? "opacity-100" : "opacity-0"}`}
        style={{ transitionDelay: fullyOpen ? "0.25s" : "0s" }}
      >
        <CanOnTable product={PRODUCT_DRINK} leftPct={41.5} delay="0.30s" testId="vault-product-drink" />
        <CanOnTable product={PRODUCT_DESSERT} leftPct={58.5} delay="0.50s" testId="vault-product-dessert" />

        {/* captions sit outside the chamber footprint */}
        <div className="absolute inset-x-0 bottom-3 md:bottom-5 z-30 grid grid-cols-2 gap-2 px-4 text-center text-bone">
          <figcaption data-testid="vault-caption-drink" className="px-2">
            <p className="font-display text-base md:text-xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">{PRODUCT_DRINK.name}</p>
            <p className="text-bone/85 text-[10px] md:text-xs uppercase tracking-[0.24em] mt-1 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">{PRODUCT_DRINK.caption}</p>
          </figcaption>
          <figcaption data-testid="vault-caption-dessert" className="px-2">
            <p className="font-display text-base md:text-xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">{PRODUCT_DESSERT.name}</p>
            <p className="text-bone/85 text-[10px] md:text-xs uppercase tracking-[0.24em] mt-1 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">{PRODUCT_DESSERT.caption}</p>
          </figcaption>
        </div>
      </div>
    </div>
  );
}

/* A single can placed on the wooden table, with a subtle contact shadow + float */
function CanOnTable({ product, leftPct, delay, testId }) {
  // The wooden table top in the interior image sits at ~62% from the top of the image.
  // We anchor the can's bottom to that y coordinate so it visually rests on the table.
  return (
    <div
      data-testid={testId}
      className="absolute"
      style={{
        left: `${leftPct}%`,
        top: "70%",
        transform: "translate(-50%, -100%)",
        width: "min(18%, 118px)",
      }}
    >
      {/* contact shadow on the table */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          bottom: "-6px",
          width: "80%",
          height: "10px",
          background: "radial-gradient(ellipse at center, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 60%, transparent 80%)",
          filter: "blur(3px)",
        }}
      />
      <img
        src={product.src}
        alt={product.name}
        draggable={false}
        className="vault-can-img relative w-full h-auto block"
        style={{
          animationDelay: delay,
          filter: "drop-shadow(0 8px 10px rgba(0,0,0,0.55)) drop-shadow(0 0 16px rgba(255,224,160,0.18))",
        }}
      />
    </div>
  );
}

/* The two doors */
// NOTE: the original CSS-built Door/Wheel/Pedestal/old-VaultInterior were removed
// when the vault was switched to use the real Pumabilt door + interior artwork.

function Sparkles({ active }) {
  // 14 small static positions; CSS animation pulses them.
  const dots = useMemo(
    () =>
      Array.from({ length: 14 }).map((_, i) => ({
        id: `sparkle-${i}`,
        left: `${(i * 73) % 100}%`,
        top: `${(i * 37) % 90 + 5}%`,
        delay: `${(i % 6) * 0.18}s`,
        size: 4 + ((i * 3) % 6),
      })),
    []
  );
  if (!active) return null;
  return (
    <div className="absolute inset-0 pointer-events-none z-[35]">
      {dots.map((d) => (
        <span
          key={d.id}
          className="vault-sparkle"
          style={{
            left: d.left,
            top: d.top,
            width: d.size,
            height: d.size,
            animationDelay: d.delay,
          }}
        />
      ))}
    </div>
  );
}

/* ----------------------- 2) Standing Menu --------------------- */
function StandingMenu() {
  // split 4 cold brews — 2 left, 2 right
  const left = COLD_BREWS.slice(0, 2);
  const right = COLD_BREWS.slice(2, 4);

  return (
    <section data-testid="standing-menu" className="border-t border-bone/10 px-6 md:px-10 py-24 md:py-32">
      <div className="mx-auto max-w-[1400px]">
        <p className="label-eyebrow">§ II · The Standing Menu</p>
        <h2 className="font-display text-bone text-4xl md:text-6xl lg:text-7xl mt-5 leading-[0.95]">
          Permanent fixtures, <em className="not-italic text-brass">always served.</em>
        </h2>
        <p className="mt-6 max-w-2xl text-bone/60">
          The cold brews never leave. Slow steeped 18 hours, served over hand-cut obsidian ice.
        </p>

        <div className="mt-14 grid md:grid-cols-2 gap-x-12 gap-y-2">
          <Column items={left} colKey="L" />
          <Column items={right} colKey="R" />
        </div>
      </div>
    </section>
  );
}

function Column({ items, colKey }) {
  return (
    <ul className="divide-y divide-bone/10" data-testid={`standing-col-${colKey}`}>
      {items.map((item, idx) => (
        <li
          key={item.name}
          data-testid={`standing-item-${colKey}-${idx}`}
          className="py-7 grid grid-cols-[1fr_auto] gap-x-6 gap-y-2"
        >
          <div>
            <div className="flex items-baseline gap-3">
              <span className="font-display text-bone text-2xl md:text-3xl">{item.name}</span>
              <span className="text-bone/35 text-[10px] uppercase tracking-[0.3em]">
                N° {String(idx + 1).padStart(2, "0")}
              </span>
            </div>
            <p className="mt-2 text-bone/60 max-w-xl text-sm md:text-base leading-relaxed">{item.desc}</p>
          </div>
          <span className="font-display text-brass text-3xl md:text-4xl">${item.price}</span>
        </li>
      ))}
    </ul>
  );
}

/* ----------------------- 3) Dispatch --------------------- */
function Dispatch() {
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error("Mobile number required.");
      return;
    }
    if (!consent) {
      toast.error("Please check the SMS consent box.");
      return;
    }
    setSubmitting(true);
    try {
      await joinHouseAlerts({
        name: "Dispatch Subscriber",
        phone: phone.trim(),
        sms_opt_in: true,
      });
      setSent(true);
      toast.success("You're on the Dispatch.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="dispatch" className="border-t border-bone/10 px-6 md:px-10 py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] grid md:grid-cols-12 gap-12 md:gap-20 items-start">
        <div className="md:col-span-6">
          <p className="label-eyebrow">§ IV · The Dispatch</p>
          <h2 className="font-display text-bone text-5xl md:text-7xl lg:text-[88px] mt-6 leading-[0.92]">
            Drops vanish<br />
            <em className="not-italic text-lavender">before sunrise.</em>
          </h2>
          <p className="mt-8 max-w-md text-bone/65 leading-relaxed">
            Hand us your number. We&apos;ll whisper when the vault is about to open. Sparingly. Never
            more than twice a fortnight.
          </p>
        </div>

        <div className="md:col-span-6">
          {sent ? (
            <div data-testid="dispatch-success" className="border border-brass/40 bg-espresso/20 p-8">
              <p className="label-eyebrow text-brass">Subscribed</p>
              <p className="font-display text-bone text-3xl mt-3">The Dispatch is yours.</p>
              <p className="text-bone/70 mt-3 max-w-md">
                We&apos;ll text before the next drop. Reply <span className="text-brass">STOP</span> any time.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="dispatch-form" className="space-y-8">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-brass mb-3">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  data-testid="dispatch-input-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="w-full bg-transparent border-0 border-b border-bone/25 focus:border-brass outline-none py-4 text-bone placeholder-bone/30 font-display text-2xl md:text-3xl tracking-wide"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="dispatch-sms-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  required
                  className="mt-1 h-4 w-4 shrink-0 appearance-none border border-bone/40 bg-transparent checked:bg-brass checked:border-brass focus:outline-none focus:ring-1 focus:ring-brass cursor-pointer relative
                             checked:after:content-['✓'] checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center checked:after:text-obsidian checked:after:text-[11px] checked:after:leading-none"
                />
                <span className="text-bone/55 text-[11px] leading-relaxed">
                  By checking this box, you agree to receive recurring automated promotional and personalized marketing text messages from
                  THE UNCANNY HOUSE LLC. Consent is not a condition of purchase. Message frequency varies. Message and data rates may apply.
                  Reply STOP to unsubscribe and HELP for assistance.
                </span>
              </label>

              <button
                type="submit"
                data-testid="dispatch-submit"
                disabled={submitting}
                className="inline-flex items-center justify-center px-10 py-5 border border-brass text-brass text-[12px] uppercase tracking-[0.34em] hover:bg-brass hover:text-obsidian transition-colors disabled:opacity-60"
              >
                {submitting ? "Joining…" : "Enter the Dispatch"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   Vault-specific styles — keyframes & textures
   ========================================================= */
function VaultStyles() {
  return (
    <style>{`
      @keyframes vault-sparkle {
        0%   { opacity: 0; transform: scale(0.4) rotate(0deg); }
        50%  { opacity: 1; transform: scale(1) rotate(45deg); }
        100% { opacity: 0; transform: scale(0.4) rotate(90deg); }
      }
      @keyframes vault-can-in {
        0%   { opacity: 0; transform: translateY(18px) scale(0.96); filter: blur(4px); }
        100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
      }
      @keyframes vault-can-float {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(-3px); }
      }
      @keyframes vault-wheel-spin {
        0%   { transform: rotate(0deg); }
        70%  { transform: rotate(960deg); }
        100% { transform: rotate(1080deg); }
      }

      .vault-can-img {
        animation: vault-can-in 0.9s cubic-bezier(0.2, 0.9, 0.25, 1) both,
                   vault-can-float 6s ease-in-out 1.0s infinite;
      }

      .vault-sparkle {
        position: absolute;
        display: block;
        background: radial-gradient(circle, rgba(255,235,180,1) 0%, rgba(176,141,70,0.6) 50%, transparent 75%);
        border-radius: 50%;
        filter: blur(0.3px) drop-shadow(0 0 6px rgba(255,220,140,0.8));
        animation: vault-sparkle 2.4s ease-in-out infinite;
      }

      .vault-door-half {
        will-change: transform;
      }

      /* ===== Brass wheel overlay ===== */
      .vault-wheel-img {
        will-change: transform;
      }

      /* NOTE: the vault sequence is the centrepiece of this page so we keep its
         spin + door-swing motion ON even when the OS-level prefers-reduced-motion
         is set. Other page animations still respect the preference. */
    `}</style>
  );
}
